// Self-contained UI module for the auth flow:
//   • Sign-in form
//   • First-time password change form (Cognito FORCE_CHANGE_PASSWORD challenge)
//   • Error display, panel visibility, focus management
//
// Public API is exactly two functions: `initAuthUI` and `signOutAndReload`.
// Everything else (DOM queries, form handlers, password validation, error
// mapping) is private to this module so the rest of the app never needs
// to know how authentication is presented.
//
// On successful sign-in the configured `onSignedIn` callback is invoked
// with the user's email; the consumer is responsible for showing the rest
// of the app. On sign-out the page is reloaded for guaranteed clean state.

import {
  getCurrentSession,
  signIn,
  signOut,
  requestPasswordReset,
  confirmPasswordReset,
} from './auth.js';
import { revealDebugIfEnabled } from '../core/debug-nav.js';

const $ = (sel) => document.querySelectorAll(sel);

// Hold module-internal state until the user completes the new-password challenge.
let pendingCredentials = null;
// Email captured during the forgot-password "send code" step, reused when the
// user submits the code + new password.
let resetEmail = null;

/**
 * Bootstrap the auth UI. Wires up form submit handlers and resolves the
 * existing session (if any).
 *
 * @param onSignedIn function to call when auth has registered a signed-in user
 */
export async function initAuthUI(onSignedIn = (_session) => {}) {
  const els = grabElements();

  async function showApp(session) {
    if (!session) {
      session = await getCurrentSession();
    }
    els.userEmail.textContent = session?.email ?? '';
    els.userInfo.classList.remove('is-hidden');
    showNonAuthPanels(els);
    // Reveal the Debug nav category + home section if the user enabled it. Fire-
    // and-forget: it must not block or fail the page's own sign-in handler.
    revealDebugIfEnabled(session);
    await onSignedIn(session);
  }

  wireSignInForm(els, showApp);
  wireNewPasswordForm(els, showApp);
  wireResetFlow(els);

  els.signoutBtn.addEventListener('click', signOutAndReload);

  // Check for an existing session first — if the user is already signed in,
  // skip the sign-in form entirely.
  const session = await getCurrentSession();
  if (session) {
    await showApp(session);
    return;
  }
  showAuthPanel(els, els.authPanel);

  if (els.signinForm) {
    els.signinForm.querySelector('input[name="email"]').focus();
  }
}

/**
 * Sign out and reload the page. Reload is the simplest way to guarantee
 * no stale state hangs around in module-level variables, the items list,
 * or the AWS SDK client cache.
 */
export async function signOutAndReload() {
  try {
    await signOut();
  } finally {
    window.location.reload();
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function grabElements() {
  return {
    authPanel: $('#auth-panel')[0],
    newPasswordPanel: $('#new-password-panel')[0],
    resetPanel: $('#reset-panel')[0],
    signinForm: $('#signin-form')[0],
    newPasswordForm: $('#new-password-form')[0],
    resetRequestForm: $('#reset-request-form')[0],
    resetConfirmForm: $('#reset-confirm-form')[0],
    forgotPasswordLink: $('#forgot-password-link')[0],
    backToSigninLink: $('#back-to-signin-link')[0],
    authError: $('#auth-error')[0],
    newPasswordError: $('#new-password-error')[0],
    resetError: $('#reset-error')[0],
    resetMessage: $('#reset-message')[0],
    userInfo: $('#user-info')[0],
    userEmail: $('#user-email')[0],
    signoutBtn: $('#signout-btn')[0],
    requiresAuth: $('.requires-auth'),
    authSections: $('section.auth'),
  };
}

function showAuthPanel(els, target) {
  els.authSections.forEach((panel) => panel.classList.toggle('is-hidden', panel !== target));
}

function hideAuthPanels(els) {
  els.authSections.forEach((e) => e.classList.add('is-hidden'));
}

function showNonAuthPanels(els) {
  els.requiresAuth.forEach((e) => e.classList.remove('requires-auth'));
}

function wireSignInForm(els, onSignedIn) {
  if (!els.signinForm) {
    return;
  }
  els.signinForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    els.authError.textContent = '';
    const { email, password } = Object.fromEntries(new FormData(els.signinForm));
    try {
      const result = await signIn({ email, password });
      if (result.status === 'newPasswordRequired') {
        // Stash the credentials so we can resume the challenge once the user
        // supplies a new password.
        pendingCredentials = { email, password };
        showAuthPanel(els, els.newPasswordPanel);
        els.newPasswordForm.querySelector('input[name="newPassword"]').focus();
        return;
      }
      els.signinForm.reset();
      hideAuthPanels(els);
      await onSignedIn();
    } catch (err) {
      // An admin "Reset password" leaves the user in RESET_REQUIRED; route
      // them straight into the reset flow instead of a dead-end message.
      if ((err?.code || err?.name) === 'PasswordResetRequiredException') {
        startReset(els, email);
        return;
      }
      els.authError.textContent = friendlyAuthError(err);
    }
  });
}

/**
 * Switch to the reset panel and immediately request a code for `email`.
 * Shared by the "Forgot password?" link and the RESET_REQUIRED sign-in path.
 */
async function startReset(els, email) {
  showAuthPanel(els, els.resetPanel);
  resetClearMessages(els);
  els.resetConfirmForm.classList.add('is-hidden');
  els.resetRequestForm.classList.remove('is-hidden');
  if (email) {
    els.resetRequestForm.querySelector('input[name="email"]').value = email;
  }
  els.resetRequestForm.querySelector('input[name="email"]').focus();
}

function resetClearMessages(els) {
  els.resetError.textContent = '';
  els.resetMessage.textContent = '';
}

function wireResetFlow(els) {
  if (!els.resetPanel) {
    return;
  }

  els.forgotPasswordLink?.addEventListener('click', (e) => {
    e.preventDefault();
    const email = els.signinForm?.querySelector('input[name="email"]').value.trim();
    startReset(els, email);
  });

  els.backToSigninLink?.addEventListener('click', (e) => {
    e.preventDefault();
    showAuthPanel(els, els.authPanel);
    els.signinForm?.querySelector('input[name="email"]').focus();
  });

  // Step 1: send a verification code to the email.
  els.resetRequestForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    resetClearMessages(els);
    const { email } = Object.fromEntries(new FormData(els.resetRequestForm));
    try {
      await requestPasswordReset(email);
      resetEmail = email;
      els.resetMessage.textContent = `A verification code was sent to ${email}.`;
      els.resetConfirmForm.classList.remove('is-hidden');
      els.resetConfirmForm.querySelector('input[name="code"]').focus();
    } catch (err) {
      els.resetError.textContent = friendlyAuthError(err);
    }
  });

  // Step 2: confirm the code + new password.
  els.resetConfirmForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    resetClearMessages(els);
    const { code, newPassword, confirmNewPassword } = Object.fromEntries(
      new FormData(els.resetConfirmForm),
    );
    const validationError = validateNewPassword(newPassword, confirmNewPassword);
    if (validationError) {
      els.resetError.textContent = validationError;
      return;
    }
    if (!resetEmail) {
      els.resetError.textContent = 'Session expired. Please request a new code.';
      return;
    }
    try {
      await confirmPasswordReset({ email: resetEmail, code, newPassword });
      resetEmail = null;
      els.resetRequestForm.reset();
      els.resetConfirmForm.reset();
      els.resetConfirmForm.classList.add('is-hidden');
      // Send them back to sign in with a success note.
      showAuthPanel(els, els.authPanel);
      els.authError.textContent = '';
      const emailInput = els.signinForm?.querySelector('input[name="email"]');
      if (emailInput) emailInput.focus();
    } catch (err) {
      els.resetError.textContent = friendlyAuthError(err);
    }
  });
}

function wireNewPasswordForm(els, onSignedIn) {
  if (!els.newPasswordForm) {
    return;
  }
  els.newPasswordForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    els.newPasswordError.textContent = '';
    const { newPassword, confirmNewPassword } = Object.fromEntries(
      new FormData(els.newPasswordForm),
    );
    const validationError = validateNewPassword(newPassword, confirmNewPassword);
    if (validationError) {
      els.newPasswordError.textContent = validationError;
      return;
    }
    if (!pendingCredentials) {
      // Should not be reachable: the panel can only be shown after a sign-in
      // attempt populates `pendingCredentials`. Defensive guard.
      els.newPasswordError.textContent = 'Session expired. Please sign in again.';
      showAuthPanel(els, els.authPanel);
      return;
    }
    try {
      const { email, password } = pendingCredentials;
      await signIn({ email, password, newPassword });
      pendingCredentials = null;
      els.newPasswordForm.reset();
      hideAuthPanels(els);
      await onSignedIn();
    } catch (err) {
      els.newPasswordError.textContent = friendlyAuthError(err);
    }
  });
}

/**
 * Client-side validation that mirrors Cognito's password policy (min 8 chars,
 * one lowercase letter, one digit). Catches obvious mistakes before sending
 * the request; Cognito enforces the same rules server-side.
 */
function validateNewPassword(newPassword, confirmNewPassword) {
  if (newPassword !== confirmNewPassword) return 'Passwords do not match.';
  if (newPassword.length < 8) return 'Password must be at least 8 characters.';
  if (!/[a-z]/.test(newPassword)) return 'Password must contain a lowercase letter.';
  if (!/[0-9]/.test(newPassword)) return 'Password must contain a digit.';
  return null;
}

/**
 * Map Cognito errors to UI-friendly strings.
 *
 * NotAuthorizedException and UserNotFoundException are deliberately
 * collapsed to a single message so the API doesn't disclose which
 * accounts exist.
 */
function friendlyAuthError(err) {
  switch (err?.code || err?.name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'PasswordResetRequiredException':
      return 'Password reset is required. Contact the admin.';
    case 'UserNotConfirmedException':
      return 'Your account is pending admin approval.';
    case 'InvalidPasswordException':
      return err.message || 'New password does not meet policy requirements.';
    case 'CodeMismatchException':
      return 'That verification code is incorrect. Please check and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Please request a new one.';
    case 'LimitExceededException':
      return 'Too many attempts. Please try again in a few minutes.';
    default:
      return err.message || String(err);
  }
}
